import TagManager from 'react-gtm-module';
import { GA4_EVENT_NAMES } from './constants';
import {
  calculateDiscount,
  createCategoryPath,
  determineLowInventoryStatus,
} from './utils';
import { productDescriptionComingSoon } from '@utils/gtm/ga4/utils';
import {
  PriceGroup,
  ProductTag,
  VariantInventory,
} from '@adapters/bigcommerce/types/product';
import { Breadcrumbs } from '@adapters/bigcommerce/types/product';
import type { AtcSource } from '@utils/cart/atcSource';
// import { getPTTVideoCookie } from '@utils/videos';
// import { getPDPVideosCookie } from '@utils/videos';
// import { VideoEvent } from './trackVideoEvents';
export interface TrackCartEventItem {
  hasComparison?: boolean;
  brand?: { name?: string } | null;
  breadcrumbs?: Breadcrumbs[];
  description?: string;
  price?: PriceGroup;
  selectedColour?: { name?: string };
  selectedSize?: { name?: string | null };
  priceType?: string;
  gaName: string;
  productCode: string;
  quantity: number;
  selectedVariantSku?: string | null;
  gender?: string | null;
  inventoryAggregated?: VariantInventory | null;
  sticker?: string;
  isCanadianBrand?: boolean;
  madeInCanada?: boolean;
  isMarketplacer?: boolean;
  isDropShip?: boolean;
  hasMemberPriceDifference?: boolean;
  productTags?: ProductTag[];
  dealsBanner?: string[] | null;
  /** Button the item was added from (ED-555); the param is omitted when absent. */
  atcSource?: AtcSource;
}

interface TrackCartEventProps {
  eventName: string;
  item: TrackCartEventItem;
  extraAttributes?: {
    cart_type: string;
    /** Surface the add originated from, e.g. 'plp' | 'search' for Quick Add. */
    add_to_cart_source?: string;
    /**
     * Originating list for revenue attribution — for Quick Add this is the PLP
     * URL (path + query) the add fired from. Set on both `ecommerce` and each
     * item (mirrors `trackProductEvent`), so GA4 populates `item_list_name`
     * however the GTM tag reads it.
     */
    item_list_name?: string;
    /** Co-op brand reported for GA4 (ED-447) — set on both `ecommerce` and
        each item, same `item_list_name` mirroring, for the same reason. */
    coop_brand?: string | null;
  };
}

const { ECOM_INTERACTION, CURRENCY_CAD } = GA4_EVENT_NAMES;

const adaptItem = (
  item: TrackCartEventItem,
  itemListName?: string,
  coOpBrand?: string | null
) => {
  let noItemDescription;
  if (item.description) {
    noItemDescription = productDescriptionComingSoon(item.description);
  }

  const category = item?.breadcrumbs && createCategoryPath(item.breadcrumbs);

  const { discount, discountTier } = calculateDiscount(
    item?.price?.basePrice?.value ?? 0,
    item?.price?.price?.value ?? 0
  );

  return {
    item_id: item?.productCode ?? null,
    item_name: item?.gaName ?? null,
    item_variant: item?.selectedVariantSku ?? null,
    price: item.price?.price?.value ?? null,
    quantity: item.quantity ?? null,
    item_description_coming_soon: noItemDescription ?? null,
    discount: discount || null,
    item_gender: item?.gender ?? null,
    item_colour: item?.selectedColour?.name ?? null,
    item_size: item?.selectedSize?.name ?? null,
    item_sticker: item?.sticker?.toLowerCase() ?? null,
    discount_tier: discountTier ?? null,
    item_brand: item?.brand?.name ?? null,
    item_category: category ?? null,
    item_low_inventory_callout: determineLowInventoryStatus(item),
    item_canadian_brand: item?.isCanadianBrand ?? false,
    item_canadian_made: item?.madeInCanada ?? false,
    is_marketplacer: (item?.isDropShip || item?.isMarketplacer) ?? false,
    compare_grid: item?.hasComparison ?? false,
    is_member_pricing: item?.hasMemberPriceDifference || false,
    item_tags: item?.productTags?.map((tag) => tag?.name ?? null) ?? null,
    deals_banner: item?.dealsBanner ?? null,
    item_list_name: itemListName ?? null,
    coop_brand: coOpBrand ?? null,
    // Left out entirely (not `null`) when there's no source, per ED-555.
    ...(item?.atcSource && { atc_source: item.atcSource }),
  };
};

const trackAddToCartEvent = ({
  eventName = '',
  item,
  extraAttributes,
}: TrackCartEventProps) => {
  const adaptedItem = adaptItem(
    item,
    extraAttributes?.item_list_name,
    extraAttributes?.coop_brand
  );
  TagManager.dataLayer({ dataLayer: { ecommerce: null } }); // Clear ecommerce object

  const itemValue = (item?.price?.price?.value ?? 0) * (item.quantity ?? 0);

  // commented this out for now as this may not be required, but to be tested ER-5564
  // const videos: VideoEvent[] = [];
  // const pdpVideos = getPDPVideosCookie();
  // const pttVideos = getPTTVideoCookie();

  // if (pttVideos) {
  //   videos.push(...pttVideos);
  // }

  // if (pdpVideos) {
  //   const currentPathEvents =
  //     pdpVideos[`${window.location.pathname}-product gallery`]?.videoEvent ||
  //     pdpVideos[`${window.location.pathname}-expert advice`]?.videoEvent;
  //   if (currentPathEvents) {
  //     try {
  //       videos.push(JSON.parse(currentPathEvents));
  //     } catch (error) {
  //       console.error('Error parsing video event', error);
  //     }
  //   }
  // }

  TagManager.dataLayer({
    dataLayer: {
      event: ECOM_INTERACTION,
      event_name: eventName,
      ecommerce: {
        currency: CURRENCY_CAD,
        value: Number(itemValue.toFixed(2)) || null,
        items: [adaptedItem],
        ...extraAttributes,
      },
    },
  });
};

export default trackAddToCartEvent;
